import { reportBelongsToSite } from '../../lib/reportSiteIdentity';
/**
 * NewReportWizard — two-to-three stage report creation flow.
 *
 * Step 1 (type):           ReportTypeSelectStep — choose the report type.
 * Step 2 (disambiguation): ReportDisambiguationStep — only when a prior report
 *                          of the same type exists. User picks update / replace /
 *                          add-another (Workflow Prompt 5).
 * Step 3 (preferences):    ReportPreferencesStep — fill in per-type preferences,
 *                          review preflight, then dispatch generation via the
 *                          store action `generateTypedReport` with mode +
 *                          parentReportId so the store can branch on save.
 *
 * Workflow Prompt 3 — all three steps now render inside a single
 * shared <WizardFrame> so the wizard has a unified header + step
 * strip, one persistent backdrop, consistent Escape / Tab semantics,
 * and a single Cancel/Back footer instead of three independent
 * `fixed inset-0` modals.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { isUSCoordinate } from '../../services/dataProviders';
import { useStore } from '../../store';
import { STABLE_EMPTY_RECORD } from '../../store/stableEmpty';
import { useGenerationHeartbeat } from '../../store/generationHeartbeat';
import { ReportTypeSelectStep } from './ReportTypeSelectStep';
import { ReportPreferencesStep, type WizardMode } from './ReportPreferencesStep';
import { ReportDisambiguationStep } from './ReportDisambiguationStep';
import { WizardFrame } from './WizardFrame';
import { GenerationProgress } from './GenerationProgress';
import { SentimentEvidenceHoldNotice, useSentimentEvidenceHold } from './SentimentEvidenceNotice';
import { SENTIMENT_EVIDENCE_WAITING_NOTICE } from '../../lib/sentiment/sentimentEvidenceReadiness';
import { Button } from '../ui/button';
import { openConfirmDialog } from '../ui/confirmDialog';
import { toast } from 'sonner';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { getReportType, type ReportTypeId } from '../../config/reportTypes';
import type { ExecutiveSummaryPreferences, ProjectIntent, SiteRagReport, TypedPreferencesByType } from '../../types/siteRag';
import { preferencesFromIntent } from '../../lib/intentToPreferences';
import { defaultExecutiveSummarySourcePrefs } from '../../lib/buildExecutiveSummarySourceDigest';
import { resolveWizardPreferencesForType } from '../../lib/coverage/resolveReportSetupFields';
import { IntentPrefillBanner } from './IntentPrefillBanner';
import { ProviderCoverageBanner } from './ProviderCoverageBanner';
import { SiteStateRequiredBanner } from './SiteStateRequiredBanner';
import { CitationDensityOverrideBanner } from './CitationDensityOverrideBanner';
import type { ProviderCoverageReport } from '../../lib/audit/providerCoverageGate';
import type { CitationDensitySnapshot } from '../../lib/audit/citationDensityGate';
import {
  clearReportDialogDraft,
  findReportDialogDraftsForSite,
  isNonTrivialDraft,
  saveGeneratingDraft,
  type ReportDialogDraft,
} from '../../lib/reportDialogDraft';
import { useGenerationBeforeUnloadGuard } from '../../lib/useGenerationBeforeUnloadGuard';
import { ContextualGuideTip } from '../reportGuide/ContextualGuideTip';
import { loadReportGuideProgress, type ReportGuideProgress } from '../reportGuide/reportGuideStore';
import { auth } from '../../firebase';
import { evaluatePersistedTestFitWorkflow } from '../../lib/generationImpact/testFitWorkflow';
import {
  buildTestFitDependencyChain,
  pathActionForTestFitWorkflowCode,
  type ReportPlanItem,
  type PathAction,
} from '../../lib/pathToGo/reportPlan';
import { SCENARIO_ROUTE } from '../../lib/pathToGo/stageBoards';
import type { GenerationWorkflowStageId } from './generationImpact/GenerationWorkflowNavigator';
import { TestFitDependencyChain } from './generationImpact/TestFitDependencyChain';

type DirtyField = 'preferences' | 'documents' | 'archetype';

interface Props {
  open: boolean;
  onClose: () => void;
  site: SiteEntity;
  /** Live SED revision used to verify Test Fit's frozen publishing authority. */
  sed?: SiteEvidenceDocument | null;
  /**
   * Pre-pick a report type and skip the type-select step. Used by the
   * SiteLandingPage Reports-tab empty-state cards: clicking one opens the
   * wizard already on the preferences step (or the disambiguation step if
   * a prior report of that type exists).
   */
  initialReportType?: ReportTypeId;
  /** Close wizard and open Site Project Intent on Overview. */
  onOpenSiteIntent?: () => void;
  /** Close wizard and open Coverage Matrix tab. */
  onOpenCoverageMatrix?: () => void;
  /** Close the wizard and open Analyze / Generation & Impact. */
  onOpenGenerationImpact?: (stage?: GenerationWorkflowStageId) => void;
  /** Open the site's live Decision (successor of the retired generated guide). */
  onOpenDecisionGuide?: () => void;
  /**
   * Route a plan action the wizard does not host itself (BOM, financial
   * inputs, audit, analysis progress / QA, engineering source, a cleared
   * decision) to the tab that owns it. The host closes the wizard first.
   * `analysisType` is the analysis the wizard is preparing (the plan item the
   * action came from), so an action that names no analysis
   * (`engineering-source`) can still open that analysis's inputs.
   * Absent → those actions fall back to `onOpenDecisionGuide`.
   */
  onPlanAction?: (action: PathAction, analysisType?: ReportTypeId) => void;
  /** Runtime/entitlement availability for the Generation & Impact workspace. */
  generationImpactEnabled?: boolean;
  /** Canonical lifecycle projection shared with Analyses and Home. */
  reportPlan?: readonly ReportPlanItem[];
  /** After generation starts — close wizard and navigate host to Reports tab. */
  onHandOffToReports?: () => void;
  /**
   * U12 — force a clean start. When true the wizard ignores any in-flight
   * generation AND any saved draft for this site and lands on the Type step,
   * so "Add report" always begins a NEW report (and the user can queue
   * several) rather than resuming a stuck/previous generation. The inflight
   * "View details" path leaves this false so resume still works there.
   */
  forceNew?: boolean;
}

type Step = 'type' | 'disambiguation' | 'preferences' | 'generating';

/** Walks the site's reports to find the lineage of `latest` (chain of parentReportId). */
function lineageDepthOf(latest: SiteRagReport, allReports: SiteRagReport[]): number {
  const byId = new Map(allReports.map(r => [r.id, r]));
  let cur: SiteRagReport | undefined = latest;
  let depth = 0;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    depth++;
    cur = cur.parentReportId ? byId.get(cur.parentReportId) : undefined;
  }
  return depth;
}

export const NewReportWizard: React.FC<Props> = ({
  open, onClose, site, sed, initialReportType, onOpenSiteIntent, onOpenCoverageMatrix, onOpenGenerationImpact, onOpenDecisionGuide, onPlanAction, generationImpactEnabled, reportPlan, onHandOffToReports, forceNew,
}) => {
  const [step, setStep] = useState<Step>('type');
  const [reportType, setReportType] = useState<ReportTypeId | null>(null);
  const [mode, setMode] = useState<WizardMode>('new');
  const [existingReport, setExistingReport] = useState<SiteRagReport | undefined>(undefined);
  const [lineageDepth, setLineageDepth] = useState(1);
  // Latch to ensure the auto-jump fires once per open, not every render.
  const [autoChosenForType, setAutoChosenForType] = useState<ReportTypeId | null>(null);
  const [testFitRebuildRequested, setTestFitRebuildRequested] = useState(false);
  const testFitReadiness = useMemo(
    () => evaluatePersistedTestFitWorkflow(site, sed, { generationImpactEnabled }),
    [generationImpactEnabled, sed, site],
  );
  const testFitPlanItem = useMemo(
    () => reportPlan?.find(item => item.type === 'test-fit'),
    [reportPlan],
  );
  // P2.1 — the plan item for the chosen type, when the plan says its source
  // inputs are not ready. Step 2 shows the plan's own `blockingReason` and
  // `primaryAction` as an advisory band. Generate is deliberately NOT disabled
  // by it: the server chokepoint (`assertAnalysisPrerequisitesReady`) is the
  // one admission test, so the client can never fabricate a block the server
  // would not have raised — it can only explain the refusal before it lands.
  //
  // The two-stage lock (`stage-locked`, from `stageLockFor` — the SAME
  // predicate the chokepoint refuses with) carries no `blockingReason`: the
  // item's own inputs are complete (a locked Environmental reads `ready`). A
  // launch that skips the type step — the Reports-tab empty state or a
  // gallery "not run" card opening the wizard with `initialReportType` —
  // bypasses the disabled type card, so the band explains the lock here too
  // and routes to the scenario workspace.
  const blockingPlanItem = useMemo((): Pick<ReportPlanItem, 'type' | 'blockingReason' | 'primaryAction'> | undefined => {
    if (!reportType) return undefined;
    const item = reportPlan?.find(candidate => candidate.type === reportType);
    if (!item) return undefined;
    if (item.blockingReason) return item;
    if (item.eligibility?.reasonCode === 'stage-locked') {
      return { type: item.type, blockingReason: item.eligibility.reason, primaryAction: SCENARIO_ROUTE };
    }
    return undefined;
  }, [reportPlan, reportType]);
  const dispatchPlanAction = useCallback((action: PathAction, analysisType?: ReportTypeId) => {
    switch (action.kind) {
      case 'intent':
      case 'site-boundary':
        onOpenSiteIntent?.();
        return;
      case 'evidence':
      case 'report-setup':
      case 'sed-rebuild':
        onOpenCoverageMatrix?.();
        return;
      case 'generation-impact':
        onOpenGenerationImpact?.(action.focus === 'layout' ? 'handoff' : 'requirements');
        return;
      case 'request-access':
        toast.info('Ask a workspace administrator for edit access to this site.');
        return;
      default:
        // bom / financial-inputs / audit / report-progress / analysis-open /
        // report-qa / engineering-source / none — the host's typed router
        // opens the tab that owns each (Path to GO removal, stage 2: these
        // used to open the retired Path to GO hub).
        if (onPlanAction) onPlanAction(action, analysisType);
        else onOpenDecisionGuide?.();
    }
  }, [onOpenCoverageMatrix, onOpenDecisionGuide, onOpenGenerationImpact, onOpenSiteIntent, onPlanAction]);
  useEffect(() => {
    if (site.sedStatus === 'failed' || testFitReadiness.ready) {
      setTestFitRebuildRequested(false);
    }
  }, [site.sedStatus, testFitReadiness.ready]);

  // Workflow Prompt 4 — dirty tracking. The set holds one entry per
  // category the user touched (preferences / documents / archetype).
  // The cancel-confirm flow reads `.size` for the "{N} preferences"
  // copy and treats `size === 0` as a silent close (fresh wizard).
  const [dirtyFields, setDirtyFields] = useState<Set<DirtyField>>(() => new Set());
  const handleDirty = useCallback((field: DirtyField) => {
    setDirtyFields(prev => prev.has(field) ? prev : new Set(prev).add(field));
  }, []);

  const [guideUid, setGuideUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [guideProgress, setGuideProgress] = useState<ReportGuideProgress>(() =>
    loadReportGuideProgress(auth.currentUser?.uid ?? null),
  );
  useEffect(() => {
    const listen = auth.onAuthStateChanged;
    if (typeof listen !== 'function') return;
    const unsub = listen.call(auth, user => {
      const uid = user?.uid ?? null;
      setGuideUid(uid);
      setGuideProgress(loadReportGuideProgress(uid));
    });
    return () => unsub();
  }, []);

  // Workflow Prompt 5 — progress subscriptions. Prefer this wizard's
  // own run id when available so concurrent runs elsewhere can't bleed
  // progress/error state into this panel.
  const progress = useStore(s => s.siteRag.progress);
  const progressStage = useStore(s => s.siteRag.progressStage);
  const activeGenerations = useStore(s => s.siteRag.activeGenerations ?? STABLE_EMPTY_RECORD);
  const generationOutcomes = useStore(s => s.siteRag.generationOutcomes ?? STABLE_EMPTY_RECORD);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const currentRun = currentRunId ? activeGenerations[currentRunId] : undefined;
  // The server beat lives in its own slice, not on the run (see generationHeartbeats).
  const currentRunHeartbeatAt = useGenerationHeartbeat(currentRunId);
  const generationError = currentRunId ? (generationOutcomes[currentRunId]?.error ?? null) : null;
  // Public Sentiment held for site evidence: waiting for a running build (the
  // store dispatches it automatically) or held for a rebuild. Neither is a
  // success nor a failure, so the wizard shows the notice instead of either.
  const waitingForEvidence = Boolean(currentRun?.waitingForSiteEvidence);
  // Only a hold that still applies (a finished rebuild supersedes it).
  const siteSentimentEvidenceHold = useSentimentEvidenceHold(site.id);
  const sentimentEvidenceHold = currentRunId && siteSentimentEvidenceHold?.id === currentRunId
    ? siteSentimentEvidenceHold
    : undefined;
  /** Run id this wizard saw held for site evidence (waiting or held). */
  const [evidenceHeldRunId, setEvidenceHeldRunId] = useState<string | null>(null);
  // Held and NOT yet dispatched (waiting, held for a rebuild, or a held run's
  // dispatch still awaiting its job id). Such a run exists only in memory — a
  // closed page drops it — so it must never leave a sessionStorage
  // "generating" draft behind: with no job to settle it, the landing page would
  // render that draft as a phantom "Continuing generation…" row.
  const heldForSiteEvidence = waitingForEvidence
    || Boolean(sentimentEvidenceHold)
    || (currentRunId !== null && evidenceHeldRunId === currentRunId && !currentRun?.jobId);
  const scopedProgress = currentRun?.progress ?? progress;
  const scopedProgressStage = currentRun?.progressStage ?? progressStage;

  // Latch flipped true when this wizard dispatches a generation request.
  const [generationStarted, setGenerationStarted] = useState(false);
  const [generationSettled, setGenerationSettled] = useState(false);
  // Brief success state shown right before auto-close so the user
  // sees a positive confirmation rather than the wizard vanishing
  // mid-frame.
  const [showSuccess, setShowSuccess] = useState(false);
  /** R-71 — provider coverage block surfaced on preferences step. */
  const [providerCoverageBlock, setProviderCoverageBlock] = useState<ProviderCoverageReport | null>(null);
  /** R-3 — citation density block surfaced on preferences step. */
  const [citationDensityBlock, setCitationDensityBlock] = useState<CitationDensitySnapshot | null>(null);
  const [citationDensityRegenUsed, setCitationDensityRegenUsed] = useState(false);
  /** R-7 — site state missing block surfaced on preferences step. */
  const [siteStateRequiredBlock, setSiteStateRequiredBlock] = useState<string | null>(null);
  const pendingGenerationRef = useRef<{
    preferences: TypedPreferencesByType[ReportTypeId];
    companyArchetypeOverride?: import('../../types/company').CompanyArchetype;
    documentSelection?: { documentRefs: NonNullable<SiteRagReport['documentRefs']>; documentSelectionMode: 'auto' | 'mixed' | 'manual' };
    overrideEnabled?: boolean;
    queuedAfterEvidence?: boolean;
    providerCoverageDispatchWaivers?: { providerId: string }[];
    allowCitationDensityOverride?: boolean;
    citationDensityRegenerateFocus?: boolean;
    generationTier?: import('../../config/generationTier').GenerationTier;
  } | null>(null);

  // W23-4 — Restore Dialog draft after refresh.
  // mid-flow (picked a type, edited preferences, etc.) and a build
  // refresh tears the wizard down, the beforeunload handler below
  // writes a JSON snapshot to sessionStorage. On the next mount, the
  // restore effect reads it and stashes the draft here. A non-blocking
  // banner above the wizard body offers a one-click discard; the
  // primary "Restore" action is the wizard itself — the draft's step /
  // mode / existingReport fields have already been applied to state.
  const [restoreBanner, setRestoreBanner] = useState<ReportDialogDraft | null>(null);
  // Latch so the restore effect runs at most once per open. Without
  // this, every render would re-scan sessionStorage and (if the user
  // dismissed the banner) keep resurrecting it.
  const restoreAttemptedRef = useRef(false);
  // Suppress eager draft persist while discarding a restored snapshot so
  // useGenerationBeforeUnloadGuard doesn't immediately re-write sessionStorage.
  const suppressDraftPersistRef = useRef(false);
  const [draftPersistDisabled, setDraftPersistDisabled] = useState(false);

  // While in 'generating', watch this wizard's own run-settled latch.
  useEffect(() => {
    if (!currentRunId) return;
    const outcome = generationOutcomes[currentRunId];
    if (outcome && !generationSettled) {
      setGenerationSettled(true);
    }
  }, [currentRunId, generationOutcomes, generationSettled]);

  // R-71 — when dispatch is blocked by provider coverage, return to preferences.
  useEffect(() => {
    if (!currentRunId || !generationSettled) return;
    const outcome = generationOutcomes[currentRunId];
    if (!outcome?.providerCoverage) return;
    setProviderCoverageBlock(outcome.providerCoverage);
    setStep('preferences');
    setGenerationStarted(false);
    setGenerationSettled(false);
    setShowSuccess(false);
    setCurrentRunId(null);
  }, [currentRunId, generationOutcomes, generationSettled]);

  // R-3 — when dispatch is blocked by citation density, return to preferences.
  useEffect(() => {
    if (!currentRunId || !generationSettled) return;
    const outcome = generationOutcomes[currentRunId];
    if (!outcome?.citationDensity) return;
    setCitationDensityBlock(outcome.citationDensity);
    setCitationDensityRegenUsed(Boolean(outcome.citationDensityRegenUsed));
    setStep('preferences');
    setGenerationStarted(false);
    setGenerationSettled(false);
    setShowSuccess(false);
    setCurrentRunId(null);
  }, [currentRunId, generationOutcomes, generationSettled]);

  // R-7 — when bootstrap conflict detection requires site state, return to preferences.
  useEffect(() => {
    if (!currentRunId || !generationSettled) return;
    const outcome = generationOutcomes[currentRunId];
    if (!outcome?.siteStateRequired) return;
    setSiteStateRequiredBlock(outcome.error ?? null);
    setStep('preferences');
    setGenerationStarted(false);
    setGenerationSettled(false);
    setShowSuccess(false);
    setCurrentRunId(null);
  }, [currentRunId, generationOutcomes, generationSettled]);

  // Reload-safe async path — restore generating step when active jobs hydrate.
  React.useEffect(() => {
    // U12: a forced-new open must NOT resume an in-flight generation.
    if (!open || forceNew || step === 'generating') return;
    const runs = Object.values(activeGenerations)
      .filter(r => r.siteId === site.id);
    if (runs.length === 0) return;
    const latest = runs.sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0];
    if (!latest) return;
    setReportType((latest.reportTypeId ?? reportType ?? 'feasibility') as ReportTypeId);
    setStep('generating');
    setCurrentRunId(latest.id);
    setGenerationStarted(true);
    setGenerationSettled(Boolean(generationOutcomes[latest.id]));
    setShowSuccess(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, site.id, activeGenerations]);

  // Bind runId from a generating draft when async jobs hydrate after reload.
  React.useEffect(() => {
    if (!open || step !== 'generating' || currentRunId) return;
    const drafts = findReportDialogDraftsForSite(site.id).filter(d => d.step === 'generating');
    const draft = drafts[0];
    if (!draft?.runId) return;
    const run = activeGenerations[draft.runId];
    if (run) {
      setCurrentRunId(draft.runId);
      setGenerationStarted(true);
      setGenerationSettled(Boolean(generationOutcomes[draft.runId]));
    }
  }, [open, step, currentRunId, site.id, activeGenerations, generationOutcomes]);

  // We intentionally avoid using global `siteRag.isLoading` for the
  // completion edge because other tabs/dialogs may be generating at the
  // same time.
  useEffect(() => {
    if (step !== 'generating') return;
    if (waitingForEvidence || sentimentEvidenceHold) return;
    // A run that was held for site evidence only succeeds once it is actually
    // dispatched (it then carries a job id); until then there is no report.
    if (currentRunId && evidenceHeldRunId === currentRunId && !currentRun?.jobId) return;
    if (generationStarted && generationSettled && !generationError && !showSuccess) {
      setShowSuccess(true);
    }
  }, [step, generationError, generationStarted, generationSettled, showSuccess, waitingForEvidence, sentimentEvidenceHold, currentRunId, currentRun?.jobId, evidenceHeldRunId]);

  // Auto-close timer. Lives in its own effect keyed on showSuccess
  // alone — that way the cleanup only fires when showSuccess flips
  // back to false (or the wizard unmounts), not every time the
  // watcher above re-evaluates.
  useEffect(() => {
    if (!showSuccess) return;
    const t = setTimeout(() => {
      // Bypass the dirty-cancel confirm — generation is done; the
      // user's edits are now persisted on the new report.
      close();
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccess]);

  // Public Sentiment held for site evidence: remember the run, and close the
  // wizard (nothing was generated, nothing failed) once the held run is gone
  // without being dispatched — cancelled or its hold dismissed elsewhere.
  useEffect(() => {
    if (!currentRunId || step !== 'generating') return;
    if (waitingForEvidence || sentimentEvidenceHold) {
      setEvidenceHeldRunId(currentRunId);
      return;
    }
    if (evidenceHeldRunId === currentRunId && !currentRun && !generationOutcomes[currentRunId]) {
      setEvidenceHeldRunId(null);
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRunId, step, waitingForEvidence, sentimentEvidenceHold, currentRun, generationOutcomes, evidenceHeldRunId]);

  // W23-4 — Restore-on-mount. Scans sessionStorage for any non-stale
  // draft belonging to this site and, if one exists, re-hydrates the
  // wizard's step / type / mode state from it. Runs at most once per
  // open (latched via restoreAttemptedRef). When `initialReportType`
  // is set, the restore is scoped to that type — otherwise we take
  // whichever draft is newest. Drafts that match the open `initialReportType`
  // suppress the auto-jump effect below by pre-setting `autoChosenForType`
  // so the freshly-restored state isn't immediately clobbered.
  React.useEffect(() => {
    if (!open) {
      restoreAttemptedRef.current = false;
      suppressDraftPersistRef.current = false;
      setDraftPersistDisabled(false);
      setRestoreBanner(null);
      return;
    }
    if (restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;
    // U12: a forced-new open starts clean — skip any saved-draft restore so
    // the wizard stays on the Type step instead of re-hydrating a prior run.
    if (forceNew) return;

    const drafts = findReportDialogDraftsForSite(site.id);
    if (drafts.length === 0) return;
    const candidate = initialReportType
      ? drafts.find(d => d.reportType === initialReportType) ?? null
      : drafts[0] ?? null;
    if (!candidate) return;
    if (!isNonTrivialDraft(candidate)) {
      // Sweep the trivial draft so we don't keep tripping over it.
      clearReportDialogDraft(candidate.siteId, candidate.reportType);
      return;
    }

    // Re-hydrate wizard state. We only restore fields the wizard owns
    // — `existingReport` is looked up against the live store so we
    // never persist a stale snapshot of the parent report.
    setReportType(candidate.reportType as ReportTypeId);
    setStep(candidate.step);
    if (candidate.mode) setMode(candidate.mode);
    if (candidate.step === 'generating') {
      setCurrentRunId(candidate.runId ?? null);
      setGenerationStarted(true);
      setGenerationSettled(
        candidate.runId ? Boolean(generationOutcomes[candidate.runId]) : false,
      );
      setShowSuccess(false);
    }
    if (candidate.dirtyFields && candidate.dirtyFields.length > 0) {
      setDirtyFields(new Set(candidate.dirtyFields));
    }
    if (candidate.existingReportId) {
      const allReports = useStore.getState().siteRag.reports;
      const reportsForSite = allReports.filter(r =>
        reportBelongsToSite(site, r),
      );
      const parent = reportsForSite.find(r => r.id === candidate.existingReportId);
      if (parent) {
        setExistingReport(parent);
        setLineageDepth(lineageDepthOf(parent, reportsForSite));
      }
    }
    // Suppress the auto-jump effect — it would otherwise overwrite the
    // restored step on next render.
    setAutoChosenForType(candidate.reportType as ReportTypeId);
    if (candidate.step !== 'generating') {
      setRestoreBanner(candidate);
    }
  }, [open, initialReportType, site.id, site.name, site.reportIds, generationOutcomes]);

  // Auto-jump to disambiguation/preferences when opened with a preselected
  // type (Reports-tab empty-state card click). Must run before any conditional
  // return so the hook order stays stable across renders.
  React.useEffect(() => {
    if (!open) {
      if (autoChosenForType !== null) setAutoChosenForType(null);
      return;
    }
    if (initialReportType && initialReportType !== autoChosenForType && step === 'type') {
      const allReports = useStore.getState().siteRag.reports;
      const reportsForSite = allReports.filter(r =>
        reportBelongsToSite(site, r),
      );
      const prior = reportsForSite
        .filter(r => (r.reportType ?? 'feasibility') === initialReportType)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

      setAutoChosenForType(initialReportType);
      setReportType(initialReportType);
      if (prior) {
        setExistingReport(prior);
        setLineageDepth(lineageDepthOf(prior, reportsForSite));
        setStep('disambiguation');
      } else {
        setExistingReport(undefined);
        setMode('new');
        setStep('preferences');
      }
    }
  }, [open, initialReportType, step, autoChosenForType, site.id, site.name, site.reportIds]);

  // W23-4 — beforeunload snapshot + navigation guard during generation.
  useGenerationBeforeUnloadGuard({
    // A run held for site evidence is in-memory only (a closed page drops it),
    // so it must not leave a reload draft behind — including on the waiting ->
    // held transition, which would otherwise re-enable the eager persist.
    enabled: open && Boolean(reportType) && !draftPersistDisabled && !suppressDraftPersistRef.current && !heldForSiteEvidence,
    warnOnNavigate: step === 'generating',
    siteId: site.id,
    reportType,
    step: step === 'generating' ? 'generating' : step,
    mode,
    existingReportId: existingReport?.id,
    dirtyFields: dirtyFields.size > 0 ? Array.from(dirtyFields) : undefined,
    runId: currentRunId,
  });

  // After discard, eager persist may have re-written sessionStorage in the
  // same commit — sweep any non-generating drafts once persist is disabled.
  React.useLayoutEffect(() => {
    if (!draftPersistDisabled) return;
    for (const draft of findReportDialogDraftsForSite(site.id)) {
      if (draft.step !== 'generating') {
        clearReportDialogDraft(draft.siteId, draft.reportType);
      }
    }
  }, [draftPersistDisabled, site.id]);

  const resetWizardState = () => {
    setStep('type');
    setReportType(null);
    setMode('new');
    setExistingReport(undefined);
    setLineageDepth(1);
    setDirtyFields(new Set());
    setGenerationStarted(false);
    setGenerationSettled(false);
    setCurrentRunId(null);
    setShowSuccess(false);
    setEvidenceHeldRunId(null);
    setRestoreBanner(null);
    setProviderCoverageBlock(null);
    pendingGenerationRef.current = null;
    restoreAttemptedRef.current = false;
  };

  const close = () => {
    // Clear any persisted draft for this (site, reportType). Two
    // routes get us here:
    //   1. The user finished generating and the auto-close timer
    //      fired — the draft is now superseded by the new report.
    //   2. The user explicitly cancelled. We assume Cancel ⇒ Discard
    //      since the cancel-confirm step already gave them a chance
    //      to back out. (Leaving the draft would resurrect on next
    //      open, which is the wrong UX.)
    if (reportType) {
      clearReportDialogDraft(site.id, reportType);
    }
    resetWizardState();
    onClose();
  };

  /** Close UI during in-flight generation without clearing the session draft. */
  const closeForBackground = () => {
    if (reportType && step === 'generating' && !heldForSiteEvidence) {
      saveGeneratingDraft({
        siteId: site.id,
        reportType,
        runId: currentRunId ?? `wiz-bg-${Date.now()}`,
        mode,
        existingReportId: existingReport?.id,
        dirtyFields: dirtyFields.size > 0 ? Array.from(dirtyFields) : undefined,
      });
    }
    resetWizardState();
    onClose();
  };

  // Intent Prefill IP5 — banner state. Companion archetype + onApply
  // wiring for the IntentPrefillBanner mounted at the top of the
  // preferences step. `companyArchetypeForBanner` falls through to the
  // store's resolved companyArchetype when the site has no per-site
  // override. `handleIntentBannerApply` writes the accepted intent
  // (including fieldSource) onto the live site via `updateSite` so the
  // wizard's seed flow (preferencesFromIntent → projectIntent)
  // picks the suggestions up immediately.
  const companyArchetypeForBanner = useStore(s => s.companyArchetype) ?? undefined;
  const updateSiteForBanner = useStore(s => s.updateSite);
  const handleIntentBannerApply = useCallback(async (next: ProjectIntent) => {
    try {
      await updateSiteForBanner(site.id, { projectIntent: next });
      handleDirty('preferences');
    } catch (err) {
      toast.error(`Could not save intent suggestion: ${(err as Error)?.message ?? 'unknown error'}`);
    }
  }, [updateSiteForBanner, site.id, handleDirty]);

  /**
   * Cancel-attempt handler. WizardFrame's `onCancel` fires on
   * Escape, backdrop click, or the explicit Cancel button — all
   * three exits route through here so the confirm semantics are
   * consistent.
   *
   *   isDirty === false  →  close silently (fresh wizard, nothing
   *                          to protect).
   *   isDirty === true   →  ask "Discard your changes?" first;
   *                          only the destructive answer closes.
   */
  const handleCancelAttempt = useCallback(() => {
    // Mid-generation: skip the dirty-confirm. The user's edits are
    // already in flight as a generation request; closing the wizard
    // is "Continue in background", not a discard. Silently close +
    // surface a small toast so the user knows the work survived.
    if (step === 'generating') {
      toast.info("Report generating in background — you'll be notified when done");
      closeForBackground();
      return;
    }
    if (dirtyFields.size === 0) {
      close();
      return;
    }
    const n = dirtyFields.size;
    void openConfirmDialog({
      title: 'Discard your changes?',
      body: <>Closing now will lose the {n} {n === 1 ? 'edit' : 'edits'} you've made.</>,
      confirmLabel: 'Discard and close',
      cancelLabel: 'Keep editing',
      tone: 'danger',
    }).then(ok => { if (ok) close(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtyFields, step]);

  // Step labels — computed at the top so WizardFrame's header strip
  // gets the right shape regardless of whether disambiguation is in
  // play. When the user hasn't yet picked a type (or there's no
  // prior report for the chosen type) the strip shows the 2-step
  // shape; otherwise it shows the 3-step shape with Disambiguate in
  // the middle. Once the user clicks Generate the strip extends
  // with a final "Generating" label so the user can see at a glance
  // that the wizard hasn't gone away — generation is in flight.
  //
  //   Type → (Disambiguate) → Review  →  Generating  (Workflow Prompt 5)
  const hasPrior = !!existingReport;
  const screeningRerun = reportType === 'feasibility'
    && hasPrior
    && (mode === 'update' || mode === 'replace');
  const stepLabels = useMemo<string[]>(() => {
    const base = hasPrior ? ['Type', 'Disambiguate', 'Review'] : ['Type', 'Review'];
    return step === 'generating' ? [...base, 'Generating'] : base;
  }, [hasPrior, step]);
  const currentStepIndex = (() => {
    if (step === 'type') return 1;
    if (step === 'disambiguation') return 2;
    if (step === 'preferences') return hasPrior ? 3 : 2;
    // step === 'generating' — last slot in the extended strip.
    return stepLabels.length;
  })();
  const totalSteps = stepLabels.length;

  // Memoised early-return below — keep the hooks above unconditional.
  if (!open) return null;

  const allReports = useStore.getState().siteRag.reports;
  const siteReports = allReports.filter(r =>
    reportBelongsToSite(site, r),
  );

  const existingReportTypes = (() =>
    [...new Set(siteReports.map(r => (r.reportType ?? 'feasibility') as ReportTypeId))]
  )();

  // NWX-11 — per-type latest version, keyed by report type id. The
  // ReportTypeSelectStep surfaces this as the "Existing v{N} · next
  // v{N+1}" chip top-right on each card. Computed once per render
  // — siteReports is already sorted by `useStore.getState()` order;
  // we walk it to find the max version per type.
  const existingVersionByType: Partial<Record<ReportTypeId, number>> = (() => {
    const out: Partial<Record<ReportTypeId, number>> = {};
    for (const r of siteReports) {
      const t = (r.reportType ?? 'feasibility') as ReportTypeId;
      const v = r.version ?? 1;
      if (!out[t] || v > out[t]!) out[t] = v;
    }
    return out;
  })();

  const handleChooseType = (typeId: ReportTypeId) => {
    const prior = siteReports
      .filter(r => (r.reportType ?? 'feasibility') === typeId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    setReportType(typeId);
    if (prior) {
      setExistingReport(prior);
      setLineageDepth(lineageDepthOf(prior, siteReports));
      setStep('disambiguation');
    } else {
      setExistingReport(undefined);
      setMode('new');
      setStep('preferences');
    }
  };

  const handleDisambiguate = (chosen: Extract<WizardMode, 'update' | 'replace' | 'add-another'>) => {
    setMode(chosen);
    setStep('preferences');
  };

  const dispatchGeneration = (
    preferences: TypedPreferencesByType[ReportTypeId],
    companyArchetypeOverride?: import('../../types/company').CompanyArchetype,
    documentSelection?: { documentRefs: NonNullable<SiteRagReport['documentRefs']>; documentSelectionMode: 'auto' | 'mixed' | 'manual' },
    overrideEnabled?: boolean,
    queuedAfterEvidence?: boolean,
    providerCoverageDispatchWaivers?: { providerId: string }[],
    citationDensityOpts?: {
      allowCitationDensityOverride?: boolean;
      citationDensityRegenerateFocus?: boolean;
    },
    // Cost P3 — generation tier from ReportPreferencesStep. Undefined ⇒
    // investment-grade (the only generation path). The union still accepts
    // historical 'draft' values at this boundary so stale restored state can
    // be canonicalized by the generation service instead of crashing.
    generationTier?: import('../../config/generationTier').GenerationTier,
    sectionColumnLayouts?: Record<string, 1 | 2 | 3>,
  ) => {
    if (!reportType) return;
    if (reportType === 'test-fit' && !testFitReadiness.ready) {
      toast.error(`${testFitReadiness.title}: ${testFitReadiness.message}`);
      setStep('preferences');
      return;
    }
    pendingGenerationRef.current = {
      preferences,
      companyArchetypeOverride,
      documentSelection,
      overrideEnabled,
      queuedAfterEvidence,
      providerCoverageDispatchWaivers,
      allowCitationDensityOverride: citationDensityOpts?.allowCitationDensityOverride,
      citationDensityRegenerateFocus: citationDensityOpts?.citationDensityRegenerateFocus,
      generationTier,
    };
    const wrapped = { [reportType]: preferences } as TypedPreferencesByType;
    const parentReportId = (mode === 'update' || mode === 'replace') ? existingReport?.id : undefined;
    setStep('generating');
    setGenerationStarted(true);
    setGenerationSettled(false);
    setProviderCoverageBlock(null);
    setCitationDensityBlock(null);
    // RED-ERROR-CLEAR FIX (#8) — clear the GLOBAL "Generation Failed" sidebar
    // banner on every regenerate-start, independent of the sync/async path the
    // store action takes below. The sync path's upsertRun also nulls it, but the
    // async enqueue path can race (a slow terminal handler for the old failed
    // run may re-apply its error); clearing here makes "clears on a new gen
    // attempt" hold uniformly the instant the user dispatches.
    useStore.getState().clearGenerationError();
    const runId = `wiz-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setCurrentRunId(runId);
    setShowSuccess(false);
    saveGeneratingDraft({
      siteId: site.id,
      reportType,
      runId,
      mode,
      existingReportId: parentReportId,
      dirtyFields: dirtyFields.size > 0 ? Array.from(dirtyFields) : undefined,
    });
    // Compatibility only: ReportPreferencesStep is pinned to investment-grade,
    // but a stale restored wizard can still carry 'draft'. Thread it through so
    // the generation resolver can canonicalize it to investment-grade.
    const draftTier = generationTier === 'draft';
    const hasLayoutOverrides = sectionColumnLayouts && Object.keys(sectionColumnLayouts).length > 0;
    const expectedTestFitAuthorityRef =
      reportType === 'test-fit' && testFitReadiness.ready
        ? testFitReadiness.authority
        : undefined;
    const siteConfigOverrides =
      overrideEnabled
      || queuedAfterEvidence
      || (providerCoverageDispatchWaivers?.length ?? 0) > 0
      || citationDensityOpts?.allowCitationDensityOverride
      || citationDensityOpts?.citationDensityRegenerateFocus
      || draftTier
      || hasLayoutOverrides
      || expectedTestFitAuthorityRef
        ? {
            ...(overrideEnabled ? { typedPreferencesOverride: wrapped } : {}),
            ...(queuedAfterEvidence ? { queuedEvidenceWait: true } : {}),
            ...(providerCoverageDispatchWaivers?.length ? { providerCoverageDispatchWaivers } : {}),
            ...(citationDensityOpts?.allowCitationDensityOverride
              ? { allowCitationDensityOverride: true }
              : {}),
            ...(citationDensityOpts?.citationDensityRegenerateFocus
              ? { citationDensityRegenerateFocus: true }
              : {}),
            ...(draftTier ? { generationTier: 'draft' as const } : {}),
            ...(hasLayoutOverrides ? { sectionColumnLayouts } : {}),
            ...(expectedTestFitAuthorityRef ? { expectedTestFitAuthorityRef } : {}),
          }
        : undefined;
    void useStore.getState().generateTypedReport(
      site.id, reportType, wrapped, mode, parentReportId,
      companyArchetypeOverride, documentSelection, undefined, siteConfigOverrides, runId,
    ).finally(() => {
      setGenerationSettled(true);
    });
    onHandOffToReports?.();
  };

  const handleConfirm = async (
    preferences: TypedPreferencesByType[ReportTypeId],
    companyArchetypeOverride?: import('../../types/company').CompanyArchetype,
    documentSelection?: { documentRefs: NonNullable<SiteRagReport['documentRefs']>; documentSelectionMode: 'auto' | 'mixed' | 'manual' },
    overrideEnabled?: boolean,
    queuedAfterEvidence?: boolean,
    // Cost P3 — generation tier picked in ReportPreferencesStep.
    generationTier?: import('../../config/generationTier').GenerationTier,
    sectionColumnLayouts?: Record<string, 1 | 2 | 3>,
  ) => {
    dispatchGeneration(
      preferences,
      companyArchetypeOverride,
      documentSelection,
      overrideEnabled,
      queuedAfterEvidence,
      undefined,
      undefined,
      generationTier,
      sectionColumnLayouts,
    );
  };

  const handleRetryAfterCoverageBlock = (opts?: {
    providerCoverageDispatchWaivers?: { providerId: string }[];
  }) => {
    const pending = pendingGenerationRef.current;
    if (!pending) return;
    dispatchGeneration(
      pending.preferences,
      pending.companyArchetypeOverride,
      pending.documentSelection,
      pending.overrideEnabled,
      pending.queuedAfterEvidence,
      opts?.providerCoverageDispatchWaivers ?? pending.providerCoverageDispatchWaivers,
      undefined,
      pending.generationTier,
    );
  };

  const handleRetryAfterCitationDensityBlock = (opts?: {
    allowCitationDensityOverride?: boolean;
    citationDensityRegenerateFocus?: boolean;
  }) => {
    const pending = pendingGenerationRef.current;
    if (!pending) return;
    dispatchGeneration(
      pending.preferences,
      pending.companyArchetypeOverride,
      pending.documentSelection,
      pending.overrideEnabled,
      pending.queuedAfterEvidence,
      pending.providerCoverageDispatchWaivers,
      {
        allowCitationDensityOverride: opts?.allowCitationDensityOverride,
        citationDensityRegenerateFocus: opts?.citationDensityRegenerateFocus,
      },
      pending.generationTier,
    );
  };

  // Workflow Prompt 6 — A/B comparison seed.
  //
  // 'update' / 'replace' prefill from the existing report (linear
  // edit of one lineage).
  //
  // 'add-another' is the A/B branch: the user is forking the
  // sibling to compare framings (e.g. "with renewables" vs.
  // "without"). Seeding from intent meant the form opened blank
  // and the user re-typed everything they had on the previous run.
  // We now seed from the most recent same-type sibling and only
  // fall back to intent when none exists.
  //
  // 'new' (no prior report at all) falls through to intent.
  const sameTypeSibling = (reportType && mode === 'add-another')
    ? siteReports
        .filter(r => (r.reportType ?? 'feasibility') === reportType)
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0]
    : null;
  const initialPreferences: TypedPreferencesByType[ReportTypeId] | undefined = (() => {
    if (!reportType) return undefined;
    // #25 — Decision Guide as a LIVING doc. The Executive Summary report IS
    // the Decision Guide (auto-aggregates every completed report into one
    // GO/CONDITIONAL/NO-GO call). Rather than land the user on an empty
    // source picker (a manual generate step that made the guide feel
    // un-live), seed ALL latest eligible completed sources up-front for
    // EVERY mode. A rerun (update/replace/add-another) therefore always
    // refreshes the source set to the newest reports — so re-opening the
    // guide after a new domain report auto-includes it — while preserving
    // any synthesisFocus the user previously set. Falls through to the
    // existing seed chain (and the empty-source affordance) only when the
    // site has no eligible sources yet.
    if (reportType === 'executive-summary') {
      const priorPrefs = ((): ExecutiveSummaryPreferences | undefined => {
        if (mode === 'update' || mode === 'replace') {
          return existingReport?.typedPreferences?.['executive-summary'];
        }
        return sameTypeSibling?.typedPreferences?.['executive-summary'];
      })();
      const seeded = defaultExecutiveSummarySourcePrefs(siteReports, priorPrefs?.synthesisFocus);
      if (seeded) return seeded;
      // No eligible sources — preserve any prior prefs so the empty-source
      // affordance still renders the user's framing notes.
      return priorPrefs;
    }
    if (mode === 'update' || mode === 'replace') {
      return existingReport?.typedPreferences?.[reportType];
    }
    const fromSibling = sameTypeSibling?.typedPreferences?.[reportType];
    if (fromSibling) return fromSibling;
    const fromSite = resolveWizardPreferencesForType(reportType, site);
    if (fromSite) return fromSite;
    return preferencesFromIntent(reportType, site.projectIntent, site);
  })();
  /** Origin of the seed — surfaced as a one-line subtitle in the
   *  preferences step so the user knows where the prefilled values
   *  came from. The "Reset to site defaults" link in the step body
   *  flips back to intent-seeded values regardless of origin. */
  const seedSource: 'parent' | 'sibling' | 'intent' = (() => {
    if (!reportType) return 'intent';
    if ((mode === 'update' || mode === 'replace') && existingReport?.typedPreferences?.[reportType]) {
      return 'parent';
    }
    if (sameTypeSibling?.typedPreferences?.[reportType]) return 'sibling';
    return 'intent';
  })();

  // Frame-level Back. Hooks the per-step navigation logic the legacy
  // step components used to render inline. Step 1 has no Back (Cancel
  // handles that), so this returns null on the type step.
  const handleFrameBack = () => {
    if (step === 'type') {
      return; // no-op; the Cancel button on the right is the exit
    }
    if (step === 'disambiguation') {
      setStep('type');
      setReportType(null);
      setExistingReport(undefined);
      return;
    }
    // step === 'preferences'
    if (existingReport) {
      setStep('disambiguation');
    } else {
      setStep('type');
      setReportType(null);
    }
  };

  // Footer is a left-aligned Back (when applicable) + a right-aligned
  // Cancel. The Generate / Continue CTAs live inside each step body
  // because they carry per-step state (preflight gates, choice-card
  // selection, etc.) — hoisting them would require threading too
  // much state up. Cancel + Back stay on the frame so they're
  // consistent across the three steps.
  const wizardFooter = (
    <div className="flex items-center justify-between">
      {step === 'type' ? (
        <span />
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="h-8 type-body uppercase tracking-widest"
          onClick={handleFrameBack}
          data-testid="wizard-frame-back"
        >
          <ChevronLeft className="w-3 h-3 mr-1" /> Back
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        className="h-8 type-body uppercase tracking-widest"
        onClick={handleCancelAttempt}
        data-testid="wizard-frame-cancel"
      >
        Cancel
      </Button>
    </div>
  );

  // Active step body. Each child renders without its own modal frame
  // — they were stripped to prep for this single shared shell.
  const renderBody = () => {
    if (step === 'type') {
      return (
        <ReportTypeSelectStep
          onChooseType={handleChooseType}
          existingReportTypes={existingReportTypes}
          reportPlan={reportPlan}
          /* NWX-11 — thread the site's project intent so each card's
             preview line reflects the same numbers the preferences
             step would seed from (~MW intake, NEPA pathway, etc.),
             and the per-type lineage depth so the "Existing v{N}"
             chip surfaces the version the next generate will
             produce. */
          {...(site.projectIntent ? { projectIntent: site.projectIntent } : {})}
          existingVersionByType={existingVersionByType}
        />
      );
    }
    if (step === 'disambiguation') {
      if (!reportType || !existingReport) {
        // Shouldn't happen — `step` flips back via the handler chain
        // above. Render nothing rather than crash if it slips through.
        return null;
      }
      return (
        <ReportDisambiguationStep
          reportType={reportType}
          site={site}
          latest={existingReport}
          lineageDepth={lineageDepth}
          // Replace deletes the parent, which the server refuses for a report
          // held in another member's namespace. `_ownerUid` is the transient
          // path-owner stamp the company-shared listing attaches; an unstamped
          // report is the requester's own.
          replaceAllowed={
            !existingReport._ownerUid
            || existingReport._ownerUid === (auth.currentUser?.uid ?? null)
          }
          onBack={handleFrameBack}
          onChoose={handleDisambiguate}
        />
      );
    }
    if (step === 'generating') {
      // Brief success state — fires once this run settles with
      // no error. Sits in place of the strip for 1.5s before the
      // wizard auto-closes.
      if (showSuccess) {
        return (
          <div
            className="p-10 text-center space-y-3"
            data-testid="generation-success"
            role="status"
          >
            <div className="w-16 h-16 mx-auto bg-status-success/15 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-status-success" />
            </div>
            <div className="text-sm font-bold uppercase tracking-widest text-text-primary">
              Report generated
            </div>
            <div className="text-[10px] text-text-secondary">
              Saving and switching to the new report…
            </div>
          </div>
        );
      }
      // Public Sentiment held because the site's evidence needs a rebuild (or
      // kept changing): nothing was dispatched, so this is neither a progress
      // view (no spinner, no other run's progress, no "will save
      // automatically") nor a failure. The hold stays on the Analyses row and
      // in the sidebar after the wizard closes.
      if (sentimentEvidenceHold && !currentRun) {
        return (
          <div className="p-6 space-y-4" data-testid="wizard-sentiment-evidence-held">
            <h2 className="text-sm font-bold text-text-primary">
              Public Sentiment has not started
            </h2>
            <SentimentEvidenceHoldNotice
              hold={sentimentEvidenceHold}
              data-testid="wizard-sentiment-evidence-hold"
            />
            <div className="text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={close}
                data-testid="wizard-sentiment-evidence-close"
              >
                Close
              </Button>
            </div>
          </div>
        );
      }
      return (
        <GenerationProgress
          progressStage={scopedProgressStage || 'Initializing…'}
          progress={scopedProgress}
          heartbeatAt={currentRunHeartbeatAt}
          startedAt={currentRun?.startedAt}
          estimatedDurationMs={currentRun?.estimatedDurationMs}
          title={waitingForEvidence
            ? 'Public Sentiment is waiting for site evidence'
            : `Generating ${reportType ?? 'feasibility'} report`}
          error={generationError || null}
          onCancel={waitingForEvidence && currentRunId
            ? () => {
                void useStore.getState().cancelReportGenerationJob(currentRunId);
                close();
              }
            : undefined}
          onContinueInBackground={() => {
            toast.info(waitingForEvidence
              ? SENTIMENT_EVIDENCE_WAITING_NOTICE
              : "Report generating in background — you'll be notified when done");
            closeForBackground();
          }}
        />
      );
    }
    // step === 'preferences'
    if (!reportType) return null;
    if (reportType === 'executive-summary') {
      return (
        <div className="p-5" data-testid="live-decision-guide-redirect" role="status">
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
            <p className="type-meta font-bold text-primary">
              Live decision guide
            </p>
            <h2 className="mt-1 text-sm font-bold text-text-primary">
              The Go/No-Go Decision Guide now lives on the Decision tab
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-text-secondary">
              It updates from the current intent, SED, and source reports. Historical generated guides remain readable, while exports and human decisions freeze the exact source revisions.
            </p>
            <div className="mt-4 flex justify-end">
              <Button
                type="button"
                size="sm"
                className="min-h-11"
                onClick={onOpenDecisionGuide}
                disabled={!onOpenDecisionGuide}
                data-testid="open-live-decision-guide"
              >
                Open live Decision
              </Button>
            </div>
          </div>
        </div>
      );
    }
    if (reportType === 'test-fit' && !testFitReadiness.ready) {
      const primaryAction =
        testFitPlanItem?.primaryAction
        ?? pathActionForTestFitWorkflowCode(testFitReadiness.code, true);
      const generationStage: GenerationWorkflowStageId | undefined =
        primaryAction.kind === 'generation-impact'
          ? (primaryAction.focus === 'layout' ? 'handoff' : 'requirements')
          : undefined;
      const dependencyChain =
        testFitPlanItem?.dependencyChain
        ?? buildTestFitDependencyChain(testFitReadiness, false);
      const rebuildInProgress = testFitReadiness.code === 'sed-rebuilding' || testFitRebuildRequested;
      return (
        <div className="p-5" data-testid="test-fit-generation-gate" role="status">
          <div className="border border-warning/40 bg-warning/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-warning">Test Fit authority check</p>
                <h2 className="mt-1 text-sm font-bold text-text-primary">{testFitReadiness.title}</h2>
                <p className="mt-2 text-xs leading-relaxed text-text-secondary">{testFitReadiness.message}</p>
                <div className="mt-4 rounded-lg border border-border-subtle bg-background px-3 py-3">
                  <div className="type-meta font-bold text-text-secondary">
                    Generation authority line
                  </div>
                  <TestFitDependencyChain steps={dependencyChain} />
                </div>
                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  {generationStage ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => onOpenGenerationImpact?.(generationStage)}
                      disabled={!onOpenGenerationImpact}
                      data-testid="test-fit-open-generation-impact"
                    >
                      {primaryAction.label}
                    </Button>
                  ) : primaryAction.kind === 'request-access' ? (
                    <p className="max-w-sm text-right text-[10px] leading-relaxed text-text-muted">
                      Ask a workspace administrator to enable Generation &amp; Impact for this site.
                    </p>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      disabled={rebuildInProgress}
                      onClick={() => {
                        setTestFitRebuildRequested(true);
                        void useStore.getState().rebuildSiteSed(site.id, { force: true })
                          .catch((error) => {
                            setTestFitRebuildRequested(false);
                            toast.error(`SED rebuild could not start: ${(error as Error).message || 'unknown error'}`);
                          });
                      }}
                      data-testid="test-fit-rebuild-sed"
                    >
                      {rebuildInProgress ? 'SED rebuild in progress…' : primaryAction.label}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <>
        {/* Intent Prefill IP5 — surface the same suggester as the Rerun
             wizard. On fresh sites only archetype-default / fallback
             tiers fire, but that still beats asking the user to pick
             cold. Banner self-hides when there's no value to surface
             (all fields decided OR only fallback hits). Accepts write
             through to site.projectIntent so the wizard's
             preferencesFromIntent seed picks them up instantly. */}
        <div className="px-5 pt-4 space-y-3">
          {providerCoverageBlock && reportType && (
            <ProviderCoverageBanner
              siteId={site.id}
              reportTypeId={reportType}
              report={providerCoverageBlock}
              onRetryDispatch={handleRetryAfterCoverageBlock}
              onCancel={() => setProviderCoverageBlock(null)}
            />
          )}
          {citationDensityBlock && reportType && (
            <CitationDensityOverrideBanner
              siteId={site.id}
              reportTypeId={reportType}
              snapshot={citationDensityBlock}
              regenFocusUsed={citationDensityRegenUsed}
              onRetryDispatch={handleRetryAfterCitationDensityBlock}
              onCancel={() => setCitationDensityBlock(null)}
            />
          )}
          {siteStateRequiredBlock && (
            <SiteStateRequiredBanner
              message={siteStateRequiredBlock}
              onOpenSiteIntent={onOpenSiteIntent}
              onDismiss={() => setSiteStateRequiredBlock(null)}
            />
          )}
          {blockingPlanItem?.blockingReason && (
            <div
              className="border border-warning/40 bg-warning/5 p-3"
              role="status"
              data-testid="wizard-plan-blocking-band"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-warning">Source inputs not ready</p>
                  <p className="mt-1 text-xs leading-relaxed text-text-secondary" data-testid="wizard-plan-blocking-reason">
                    {blockingPlanItem.blockingReason}
                  </p>
                  <p className="mt-1 text-[10px] leading-relaxed text-text-muted">
                    You can still press Generate; the request will be refused until this is resolved.
                  </p>
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => dispatchPlanAction(blockingPlanItem.primaryAction, blockingPlanItem.type)}
                      data-testid="wizard-plan-blocking-action"
                    >
                      {blockingPlanItem.primaryAction.label}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!screeningRerun && (
            <>
              <ContextualGuideTip
                tipId="new-report-preferences"
                stepId="generate-report"
                title="Inherited from site"
                body="Review your screening brief and add the setup needed for this report below. Shared engineering and commercial inputs live in Project Intent → Design & report assumptions."
                uid={guideUid}
                progress={guideProgress}
                onProgressChange={setGuideProgress}
              />
              <IntentPrefillBanner
                site={site}
                recentReports={siteReports}
                documents={site.documents ?? []}
                archetype={site.archetypeOverride ?? companyArchetypeForBanner}
                onApply={handleIntentBannerApply}
              />
            </>
          )}
        </div>
        <ReportPreferencesStep
          reportType={reportType}
          site={site}
          existingReport={existingReport}
          initialPreferences={initialPreferences}
          mode={mode}
          seedSource={seedSource}
          siblingReport={sameTypeSibling}
          intentDefaults={resolveWizardPreferencesForType(reportType, site)
            ?? preferencesFromIntent(reportType, site.projectIntent, site) as TypedPreferencesByType[ReportTypeId] | undefined}
          onBack={handleFrameBack}
          onConfirm={handleConfirm}
          onDirty={handleDirty}
          onOpenSiteIntent={onOpenSiteIntent}
          onOpenCoverageMatrix={onOpenCoverageMatrix}
        />
      </>
    );
  };

  // Content-Source Fixes Prompt 14 — non-US sites get a warning chip
  // at the top of every wizard step. The provider catalogue is
  // US-anchored (HIFLD, FEMA, USFWS, EIA, AirNow, NRC, etc.) so a
  // non-US site's SED relies on AI research + user docs only, and
  // the report's overall confidence is capped at 0.65. Surfacing
  // this here lets the user upload docs OR abort before paying for
  // a degraded generation.
  const isNonUS = !isUSCoordinate(site.location.lat, site.location.lng);

  return (
    <WizardFrame
      open={open}
      onCancel={handleCancelAttempt}
      title="New Report"
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      stepLabels={stepLabels}
      // Hide the frame footer during generation — GenerationProgress
      // renders its own Continue-in-background button, and Cancel /
      // Back are not meaningful mid-flight (generation is fire-and-
      // forget; the user can only let it finish or background it).
      footer={step === 'generating' ? undefined : wizardFooter}
    >
      {restoreBanner && (
        <div
          className="mb-4 px-3 py-2 border border-status-info/40 bg-status-info/10 text-status-info text-[11px] flex items-start gap-2"
          data-testid="wizard-restore-banner"
          role="status"
        >
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden />
          <span className="flex-1">
            Restored your in-progress{' '}
            {(() => {
              // Map the persisted reportType id back to its human label
              // where possible. Falls back to the raw id if the registry
              // has been pruned since the draft was written.
              try {
                return getReportType(restoreBanner.reportType as ReportTypeId).shortLabel;
              } catch {
                return restoreBanner.reportType;
              }
            })()}{' '}
            draft from before the refresh.
          </span>
          <button
            type="button"
            data-testid="wizard-restore-discard"
            className="ml-2 inline-flex items-center gap-1 type-meta hover:underline"
            onClick={() => {
              // Reset to a fresh wizard for this (site, reportType).
              // Clear the persisted snapshot AND the in-memory state
              // the restore effect just hydrated.
              suppressDraftPersistRef.current = true;
              setDraftPersistDisabled(true);
              clearReportDialogDraft(site.id, restoreBanner.reportType);
              setStep('type');
              setReportType(null);
              setMode('new');
              setExistingReport(undefined);
              setLineageDepth(1);
              setDirtyFields(new Set());
              setAutoChosenForType(null);
              setRestoreBanner(null);
            }}
          >
            <X className="w-3 h-3" aria-hidden />
            Discard
          </button>
        </div>
      )}
      {isNonUS && (
        <div
          className="mb-4 px-3 py-2 rounded-lg border border-warning/40 bg-warning/10 text-warning text-[11px] flex items-start gap-2"
          data-testid="wizard-non-us-chip"
          role="status"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden />
          <span>
            Limited US-public-API coverage for this geography. Grid,
            regulatory, environmental, and electricity-rate sections
            will rely on AI research + uploaded documents only.
            Consider uploading utility / regulatory documents before
            generating. Overall confidence is capped at 65%.
          </span>
        </div>
      )}
      {renderBody()}
    </WizardFrame>
  );
};
